// Utility functions for iframe communication between landing page editor and preview

// Message types for iframe to parent communication
export interface LandingPageMessage {
  type: 'LANDING_PAGE_MESSAGE';
  payload: {
    action:
      | 'EDIT_ELEMENT'
      | 'SAVE_CHANGES'
      | 'CANCEL_EDIT'
      | 'EDIT_STATUS_CHANGED'
      | 'REPLACE_IMAGE';
    data?: any;
  };
}

// Command types for parent to iframe communication
export interface LandingPageCommand {
  type: 'LANDING_PAGE_COMMAND';
  payload: {
    action:
      | 'EDIT_ELEMENT'
      | 'SAVE_CHANGES'
      | 'CANCEL_EDIT'
      | 'DISABLE_EDIT_MODE'
      | 'ENABLE_EDIT_MODE';
    data?: {
      selector?: string; // CSS selector for EDIT_ELEMENT action
    };
  };
}

// Utility function to send messages from iframe to parent
export const sendMessageToParent = (
  action: LandingPageMessage['payload']['action'],
  data?: any
) => {
  if (typeof window !== 'undefined' && window.parent !== window) {
    const message: LandingPageMessage = {
      type: 'LANDING_PAGE_MESSAGE',
      payload: { action, data },
    };
    window.parent.postMessage(message, '*');
  }
};

// Utility function to send commands from parent to iframe
export const sendCommandToIframe = (
  iframe: HTMLIFrameElement | null,
  action: LandingPageCommand['payload']['action'],
  data?: any
) => {
  if (iframe?.contentWindow) {
    const command: LandingPageCommand = {
      type: 'LANDING_PAGE_COMMAND',
      payload: { action, data },
    };
    iframe.contentWindow.postMessage(command, '*');
  }
};

// Type guard to check if a message is a landing page message
export const isLandingPageMessage = (
  event: MessageEvent
): event is MessageEvent<LandingPageMessage> => {
  return event.data?.type === 'LANDING_PAGE_MESSAGE';
};

// Type guard to check if a message is a landing page command
export const isLandingPageCommand = (
  event: MessageEvent
): event is MessageEvent<LandingPageCommand> => {
  return event.data?.type === 'LANDING_PAGE_COMMAND';
};

// Hook for handling iframe messages in parent components
export const createIframeMessageHandler = (onMessage: (message: LandingPageMessage) => void) => {
  return (event: MessageEvent) => {
    if (isLandingPageMessage(event)) {
      onMessage(event.data);
    }
  };
};

// Hook for handling commands in iframe components
export const createCommandHandler = (onCommand: (command: LandingPageCommand) => void) => {
  return (event: MessageEvent) => {
    if (isLandingPageCommand(event)) {
      onCommand(event.data);
    }
  };
};
